import Link from 'next/link';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserWallet } from '@/api/auth';
import { BonusStage } from '@/api/common';
import { IconComponent } from '@/components/IconComponent';
import {
  amountWithCurrencySymbol,
  DEFAULT_CURRENCIES,
} from '@/helpers/currency';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import type { IAccount } from '@/modules/common/hooks/usePlayerAccounts';
import { BONUS_REF_PARAM } from '@/modules/promotions/components/PromotionsPage/helpers';
import { useGetCommonStore } from '@/store';
import { ImageComponent } from '../../Image';
import { ProgressBar } from '../../progressBars/LinearProgressBar';
import { ProgressBarMode } from '../../progressBars/LinearProgressBar/styled';
import { Skeleton } from '../../Skeleton';

const SKELETON_HEIGHTS = {
  WAGERING: {
    MOBILE: 129,
    DESKTOP: 121,
  },
  DEFAULT: {
    MOBILE: 58,
    DESKTOP: 42,
  },
};

interface ActiveOptionProps extends UserWallet {
  account?: IAccount;
  accountPending?: boolean;
  toggleOpen: () => void;
}

export const ActiveOption: React.FC<ActiveOptionProps> = ({
  icon,
  account,
  currency,
  accountPending,
  toggleOpen,
}) => {
  const { t } = useTranslation('deposits');
  const useCommonStore = useGetCommonStore();

  const bonuses = useCommonStore.use.bonuses();
  const bonusesPending = useCommonStore.use.bonusesPending();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const optionBonuses = useMemo(
    () =>
      bonuses
        .filter(
          (b) => b.stage === BonusStage.HandleBets && b.currency === currency
        )
        .reverse(),
    [bonuses, currency]
  );

  const wageringBonus = useMemo(() => optionBonuses[0], [optionBonuses]);
  const bonusesInQueue = useMemo(
    () => (optionBonuses.length > 1 ? optionBonuses.length - 1 : 0),
    [optionBonuses]
  );

  const formatAmount = (value?: number, rounding?: boolean) => {
    const data = getCurrencyCentsAndCode(
      value || 0,
      currency,
      DEFAULT_CURRENCIES
    );

    return amountWithCurrencySymbol(data.currency, data.amount, {
      wholeRounding: rounding,
    });
  };

  const bonusAmount = useMemo(
    () => account && account.amountCents - account.availableToCashoutCents,
    [account]
  );

  const skeletonHeight = useMemo(() => {
    if (wageringBonus)
      return isMobileDevice
        ? SKELETON_HEIGHTS.WAGERING.MOBILE
        : SKELETON_HEIGHTS.WAGERING.DESKTOP;

    return isMobileDevice
      ? SKELETON_HEIGHTS.DEFAULT.MOBILE
      : SKELETON_HEIGHTS.DEFAULT.DESKTOP;
  }, [wageringBonus, isMobileDevice]);

  if (bonusesPending || accountPending || !account) {
    return (
      <Skeleton.Container borderRadius={16}>
        <Skeleton.Rect height={skeletonHeight} />
      </Skeleton.Container>
    );
  }

  return (
    <li
      className={`activeOption ${wageringBonus ? 'activeWagering' : 'notActiveWagering'}`}
      key={currency}
    >
      <div className="balanceContainer">
        <div className="tick-icon" />
        <div className="rightSide">
          <div className="balances">
            <div className="real">
              <span className="label">{t('balance.real')}</span>
              <p className="value">
                {formatAmount(account.availableToCashoutCents)}
              </p>
            </div>
            {!!bonusAmount && (
              <div className="bonus">
                <span className="label">{t('balance.bonus')}</span>
                <p className="value">{formatAmount(bonusAmount)}</p>
              </div>
            )}
          </div>
          {icon && (
            <ImageComponent alt="currency" height={24} src={icon} width={24} />
          )}
        </div>
      </div>
      {wageringBonus && (
        <>
          <div className="divider" />
          <div className="wageringBonuses">
            <div className="wageringContainer">
              <div className="wagering">
                <span className="label">{t('wagering')}</span>
                <Link
                  className="link"
                  href={`/promotions?tab=active-bonuses&${BONUS_REF_PARAM}=${wageringBonus.id}`}
                  onClick={toggleOpen}
                >
                  <span className="title">{wageringBonus.title}</span>
                  <IconComponent
                    className="arrowIcon"
                    name="whiteArrowRight"
                    size="16px"
                  />
                </Link>
              </div>
              <div className="progressBar">
                <ProgressBar
                  borderRadius="8px"
                  borderRadiusInner={'8px'}
                  from={0}
                  height={8}
                  mode={ProgressBarMode.GREEN_WHITE}
                  showTo={false}
                  to={wageringBonus.amountWagerRequirementCents}
                  value={wageringBonus.amountWagerCents}
                />
              </div>
              <div className="progressLeft">
                <span className="value">
                  {formatAmount(
                    wageringBonus.amountWagerRequirementCents -
                      wageringBonus.amountWagerCents,
                    true
                  )}
                </span>
                <span className="label">{t('wageringLeft')}</span>
              </div>
            </div>
            {!!bonusesInQueue && (
              <Link
                className="bonusesInQueue"
                href="/promotions?tab=active-bonuses"
                onClick={toggleOpen}
              >
                <span>
                  {t(
                    bonusesInQueue > 1
                      ? 'manyBonusesInQueue'
                      : 'oneBonusInQueue',
                    {
                      value: bonusesInQueue,
                    }
                  )}
                </span>
                <div className="icon">
                  <IconComponent name="whiteArrowRight" size="16px" />
                </div>
              </Link>
            )}
          </div>
        </>
      )}
    </li>
  );
};
